/**
 * Service Layer Auth Actions
 * 
 * Handles sign in, sign out, and related actions.
 */

import { authClient } from './authClient';
import { clearUserCache } from '../offline/offlineStore';
import { logScoutEvent } from '../utils/scoutLog';

export interface AuthActionsState {
  setEmail: (email: string | null) => void;
  setRole: (role: string | null) => void;
  setCsrfToken: (token: string | null) => void;
  setAuthError: (error: string | null) => void;
  setLastErrorCode: (code: string | null) => void;
  setLoading: (loading: boolean) => void;
}

/**
 * Create sign in function
 */
export function createSignIn(state: AuthActionsState, testMode: boolean) {
  return async () => {
    if (testMode) {
      state.setEmail('test@clanda.de');
      state.setAuthError(null);
      return;
    }
    
    // Redirect loop safety: check if we recently attempted login
    // Use monotonic timestamp and broader window (7-10s) for slower devices
    const OAUTH_ATTEMPT_KEY = 'oauth_attempt_ts';
    const OAUTH_ATTEMPT_WINDOW_MS = 10000; // 10 seconds (increased from 5s)
    const lastAttempt = sessionStorage.getItem(OAUTH_ATTEMPT_KEY);
    const now = Date.now();
    
    if (lastAttempt) {
      const attemptTime = parseInt(lastAttempt, 10);
      // Use monotonic check (now should always be >= attemptTime)
      if (!isNaN(attemptTime) && (now - attemptTime) < OAUTH_ATTEMPT_WINDOW_MS) {
        console.warn('[ServiceLayerAuth] Recent login attempt detected, skipping to prevent redirect loop');
        state.setLoading(false);
        return;
      }
    }
    
    // Record this attempt with monotonic timestamp
    sessionStorage.setItem(OAUTH_ATTEMPT_KEY, now.toString());
    
    state.setLoading(true);
    // Clear any previous errors when starting new login
    state.setAuthError(null);
    state.setLastErrorCode(null);
    try {
      // Use auth client package to start login
      // Preserve current path for post-login redirect
      const currentPath = window.location.pathname + window.location.search;
      const nextPath = currentPath !== '/' ? currentPath : undefined;
      
      await authClient.startLogin(nextPath);
      
      // Browser redirects automatically
      // Don't set loading to false - we're redirecting
    } catch (error) {
      console.error('Sign in failed:', error);
      state.setAuthError('Failed to start login process. Please try again.');
      state.setLoading(false);
      // Clear the attempt record on error so user can retry
      sessionStorage.removeItem(OAUTH_ATTEMPT_KEY);
    }
  };
}

/**
 * Create sign out function
 */
export function createSignOut(state: AuthActionsState, testMode: boolean) {
  return async () => {
    if (testMode) {
      state.setEmail(null);
      state.setCsrfToken(null);
      state.setAuthError(null);
      state.setLastErrorCode(null);
      return;
    }
    state.setLoading(true);
    // Read email before clearing state (needed for cache cleanup)
    const email = localStorage.getItem('appic:currentUserEmail');
    try {
      // Use auth client package to logout. This clears the SL HttpOnly cookie
      // AND the authoritative Supabase session at localStorage['sb-clanda-sl']
      // (see @appic/service-layer logout()).
      await authClient.logout();

      // Drop the email pointer too — it is outside the supabase storage key
      // namespace, and resolveCurrentUser/serviceLayerClient read it to decide
      // who is logged in. Leaving it lets the next boot re-handoff the user.
      try { localStorage.removeItem('appic:currentUserEmail'); } catch { /* storage unavailable */ }

      // DSGVO + cache hygiene: clear IDB offline data + React Query persisted cache
      if (email) {
        const cleared = await clearUserCache(email).catch(() => 0);
        logScoutEvent('cache_cleared', { email, key_count: cleared, trigger: 'signout' });
      }

      // Clear SW runtime caches (stale API responses)
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.filter(n => n.startsWith('supabase-api') || n.startsWith('service-layer')).map(n => caches.delete(n)));
      }

      // Clear local state
      state.setEmail(null);
      state.setRole(null);
      state.setCsrfToken(null);
      state.setAuthError(null);
      state.setLastErrorCode(null);
      // Redirect to login (use replace to avoid keeping authenticated page in history)
      window.location.replace('/');
    } catch (error) {
      console.warn('Logout encountered an error:', error);
      // The package logout failed mid-way; the email pointer must still be
      // dropped so a stale session can't re-handoff the user on the next boot.
      try { localStorage.removeItem('appic:currentUserEmail'); } catch { /* storage unavailable */ }
      // Still clear caches and redirect — cache clear failing here is
      // secondary to the outer logout error already being logged, but we
      // still log it so a double-failure is diagnosable.
      if (email) clearUserCache(email).catch(cacheErr => {
        logScoutEvent('auth_logout_clear_cache_failed', { error: String(cacheErr), email });
      });
      state.setEmail(null);
      state.setCsrfToken(null);
      state.setAuthError(null);
      state.setLastErrorCode(null);
      window.location.replace('/');
    } finally {
      state.setLoading(false);
    }
  };
}

